import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import AppContext from "../contexts/AppContext";
import WithLoader from "./WithLoader";
import appFetch from "../appFetch";
import { appendQueryToUri } from "../util/generalUtil";
import apiEndpoints from "../apiEndpoints";
import Select from "react-select";
import AccordionDropdown from "./AccordionDropdown";
import renderComponent from "../renderComponent";
import moment from "moment";
import { CalendarIcon } from "@heroicons/react/solid";
import ReactDatePicker from "react-datepicker";
import Spinner from "../loaders/Spinner";
import classNames from "classnames";

window._moment = moment;

const ReportOption = ({ innerProps, data, children, label }) => (
  <div className="custom-select-option" {...innerProps}>
    {children}
  </div>
);

const ReportOptionValue = ({ children }) => (
  <div className="custom-select-option-value">{children}</div>
);

function MainArea1({ setLoading, subcomponents, type }) {
  const { context, setContext } = useContext(AppContext);

  // helper: read path token directly from location when needed
  const getPathToken = () =>
    decodeURI(window.location.pathname.split("/").slice(-1)[0] || "home")
      .replace(/\//g, "")
      .replace(/\-/g, " ")
      .toLowerCase();

  // we still track currentPath so UI updates on location changes, but ensure it's updated for pushState
  const [currentPath, setCurrentPath] = useState(getPathToken());

  // patch history.pushState once and wire a locationchange event so all pushes are detected
  useEffect(() => {
    const onLocationChange = () => setCurrentPath(getPathToken());

    const origPush = window.history.pushState;
    if (!window.__pushStatePatched) {
      window.history.pushState = function (...args) {
        const ret = origPush.apply(this, args);
        // emit a safe event other parts of app can listen to
        window.dispatchEvent(new Event("locationchange"));
        return ret;
      };
      window.__pushStatePatched = true;
    }

    window.addEventListener("popstate", onLocationChange);
    window.addEventListener("locationchange", onLocationChange);
    window.addEventListener("hashchange", onLocationChange);

    return () => {
      window.removeEventListener("popstate", onLocationChange);
      window.removeEventListener("locationchange", onLocationChange);
      window.removeEventListener("hashchange", onLocationChange);
      // do NOT restore pushState here; leaving patched version is ok and safer if multiple components mount
    };
  }, []);

  // local menu + UI state
  const [menuList, setMenuList] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [newsubscomponents, setNewsubcomponents] = useState(subcomponents);

  // filters & other UI
  const [activeAccessFilter, setActiveAccessFilter] = useState(null);
  const [accessFilterData, setAccessFilterData] = useState(null);
  const [activeDateFilter, setActiveDateFilter] = useState(null);
  const [dateFilterData, setDateFilterData] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [dateRange2, setDateRange2] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [loadDate, setLoadDate] = useState(false);
  const [filterOption, setFilterOption] = useState(null);
  const [accessOption, setAccessOption] = useState(null);
  const [manualFilter, setManualFilter] = useState("");

  // to avoid race conditions between layout fetches (sequence-based)
  const layoutRequestSeq = useRef(0);

  const currentYear = new Date().getFullYear();
  const minDate = new Date(currentYear - 1, 0, 1);
  const maxDate = new Date(currentYear, 11, 31);

  // derive menu list + active menu from context + path
  useEffect(() => {
    // defensive navigation: read the list carefully
    const list =
      context?.pageConfig?.subcomponents?.[0]?.subcomponents?.[0]
        ?.subcomponents?.[1]?.options ?? [];
    setMenuList(list);

    // compute token from actual location (not stale state)
    const token = getPathToken();
    const patt = RegExp(token, "i");
    const found = list.find((m) => patt.test(m?.name)) ?? list[0] ?? null;
    setActiveMenu(found);
  }, [context?.pageConfig, currentPath]); // triggers on pageConfig changes and location changes

  // sync incoming subcomponents prop (if parent changed)
  useEffect(() => {
    setNewsubcomponents(subcomponents);
  }, [subcomponents]);

  // when activeMenu changes, reset menu-scoped state and fetch dependent dropdowns
  useEffect(() => {
    if (!activeMenu) return;

    setAccessFilterData(null);
    setActiveAccessFilter(null);
    setAccessOption(null);

    setManualFilter("");

    setDateFilterData(null);
    setActiveDateFilter(null);
    setFilterOption(null);

    setDateRange(null);
    setDateRange2(null);
    setFilterDateRange(null);

    // keep UI in sync
    setNewsubcomponents(subcomponents);

    if (activeMenu?.access_filter) {
      fetchAccessFilterDropdownData(activeMenu);
    }
    if (activeMenu?.date_filter) {
      fetchDateFilterDropdownData(activeMenu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu]);

  // fetchers accept menu as param — important to avoid closure over stale activeMenu
  const fetchAccessFilterDropdownData = useCallback(
    (menu) => {
      if (!menu) return;
      setLoading(true);

      const postData = {
        target: menu.access_filter,
        targetId: menu?.targetid,
      };

      appFetch
        .get(appendQueryToUri(apiEndpoints.dropdownView, postData))
        .then((res) => {
          setAccessFilterData(res ?? []);
          const initial = (res ?? [])[0] ?? null;
          setActiveAccessFilter(initial);
          setAccessOption(initial?.value ?? null);
        })
        .catch((e) => console.error("access filter fetch error:", e.message))
        .finally(() => setLoading(false));
    },
    [setLoading],
  );

  const fetchDateFilterDropdownData = useCallback(
    (menu) => {
      if (!menu) return;
      setLoading(true);

      const postData = {
        target: menu.date_filter,
        targetId: menu?.targetid,
      };

      appFetch
        .get(appendQueryToUri(apiEndpoints.dropdownView, postData))
        .then((res) => {
          const data = res ?? [];
          setDateFilterData(data);
          const initial = data[0] ?? null;
          setActiveDateFilter(initial);
          setFilterOption(initial?.value ?? null);
        })
        .catch((e) => console.error("date filter fetch error:", e.message))
        .finally(() => setLoading(false));
    },
    [setLoading],
  );

  const isCustomDateFilter =
    activeDateFilter?.name?.toLowerCase?.() === "custom" ||
    activeDateFilter?.value?.toLowerCase?.() === "custom";

  // keep fetchLayout generic, but serialize dateFilter when needed
  const fetchLayout = useCallback(
    async ({ dateFilter = null, accessFilter = null } = {}) => {
      if (!activeMenu) return;
      setLoading(true);
      const seq = ++layoutRequestSeq.current;

      const postData = {
        layoutId: "",
        schemaId: "",
        target: "MENU",
        targetId: activeMenu?.targetid,
        dateFilter:
          dateFilter == null
            ? null
            : typeof dateFilter === "string"
              ? dateFilter
              : JSON.stringify(dateFilter),
        accessFilter: accessFilter ?? null,
        pageSize: 50,
      };

      try {
        const res = await appFetch.get(
          appendQueryToUri(apiEndpoints.menuView, postData),
        );
        if (seq !== layoutRequestSeq.current) return;
        setNewsubcomponents(res?.subcomponents?.[0]?.subcomponents);
      } catch (e) {
        console.error("layout fetch error", e.message);
      } finally {
        if (seq === layoutRequestSeq.current) setLoading(false);
      }
    },
    [activeMenu, setLoading],
  );

  // restore the old custom-search behavior
  const filterByDateRange = (dateFilter) => {
    if (!activeMenu) return;
    setFilterOption(JSON.stringify(dateFilter));
    const accessValue = activeMenu?.manual_filter ? manualFilter.trim() : null;
    fetchLayout({ dateFilter, accessFilter: accessValue });
    setLoadDate(false);
  };

  const fetchReportNewHomeLayout = (reportType, secondaryReport) => {
    if (!activeMenu) return;

    if (reportType?.name?.toLowerCase?.() === "custom") {
      return; // custom is handled by the date picker + Search button
    }

    const accessValue = activeMenu?.manual_filter
      ? manualFilter.trim()
      : secondaryReport?.value;

    setFilterOption(reportType?.value ?? reportType?.name ?? null);
    setAccessOption(accessValue);

    fetchLayout({
      dateFilter: reportType?.value ?? reportType?.name ?? null,
      accessFilter: accessValue,
    });
  };

  const fetchAccessNewHomeLayout = (reportType, secondaryReport) => {
    if (!activeMenu) return;
    if (reportType?.name === "Custom") {
      return;
    }

    const effectiveDateFilter =
      isCustomDateFilter && filterDateRange
        ? filterDateRange
        : (secondaryReport?.value ?? null);

    setFilterOption(
      typeof effectiveDateFilter === "object" && effectiveDateFilter !== null
        ? JSON.stringify(effectiveDateFilter)
        : effectiveDateFilter,
    );
    setAccessOption(reportType?.value);
    fetchLayout({
      dateFilter: effectiveDateFilter,
      accessFilter: reportType?.value,
    });
  };

  const onManualFilterSearch = () => {
    if (!activeMenu) return;

    const manualValue = manualFilter.trim();

    const effectiveDateFilter =
      isCustomDateFilter && filterDateRange
        ? filterDateRange
        : (activeDateFilter?.value ?? null);

    setAccessOption(manualValue || "");
    setFilterOption(
      typeof effectiveDateFilter === "object" && effectiveDateFilter !== null
        ? JSON.stringify(effectiveDateFilter)
        : effectiveDateFilter,
    );

    fetchLayout({
      dateFilter: effectiveDateFilter,
      accessFilter: manualValue || "",
    });
  };
  const ondateFilterChange = (selectedOption) => {
    setActiveDateFilter(selectedOption);

    // clear old custom dates when leaving custom mode
    if (selectedOption?.name?.toLowerCase?.() !== "custom") {
      setDateRange(null);
      setDateRange2(null);
      setFilterDateRange(null);
    }

    fetchReportNewHomeLayout(selectedOption, activeAccessFilter);
  };
  const onaccessFilterChange = (selectedOption) => {
    setActiveAccessFilter(selectedOption);
    fetchAccessNewHomeLayout(selectedOption, activeDateFilter);
  };

  // derive DateReportOptions etc from current subcomponents snapshot
  const filteredDatefilter = newsubscomponents?.filter(
    (component) =>
      component.type === "DateNotificationList1" ||
      component.type === "DateNotificationList2",
  );

  const DateReportOptions =
    (filteredDatefilter ?? [])[0]?.options?.map(
      ({ period, grid, ...rest }) => ({ ...rest, label: rest.name }),
    ) || [];

  const initialActiveReport = DateReportOptions[0] ?? null;
  console.log("newsubscomponents", newsubscomponents);

  return (
    <>
      <div
        className={`lg:px-8 px-4 pb-8 pt-14 lg:pt-8 w-rb overflow-auto ${
          activeMenu?.scroll === "yes"
            ? " scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            : ""
        }`}
      >
        {/* top bar */}
        <div className="flex flex-row px-3 items-center">
          <div className="flex-1">
            {activeMenu?.access_filter && !activeMenu?.manual_filter && (
              <div className="flex gap-2 sm:w-auto">
                <Select
                  className="w-full sm:w-auto"
                  classNamePrefix="custom-select-option"
                  components={{
                    Option: ReportOption,
                    SingleValue: ReportOptionValue,
                  }}
                  value={activeAccessFilter}
                  isClearable={false}
                  onChange={onaccessFilterChange}
                  options={accessFilterData}
                />
              </div>
            )}

            {activeMenu?.manual_filter && (
              <div className="flex gap-2 w-32 items-center">
                <input
                  type="text"
                  className="w-48 rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-primary text-sm"
                  placeholder={
                    activeMenu?.manual_filter_description || "Search..."
                  }
                  value={manualFilter}
                  onChange={(e) => setManualFilter(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded-md bg-primary px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50 text-sm"
                  onClick={onManualFilterSearch}
                  // disabled={!manualFilter.trim()}
                >
                  Search
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            {(activeMenu?.access_filter ||
              activeMenu?.date_filter ||
              activeMenu?.manual_filter) && (
              <div className="flex-col">
                <div
                  className={`border-bottom flex ${
                    activeMenu?.access_filter || activeMenu?.manual_filter
                      ? "justify-between"
                      : "justify-end"
                  } items-center rounded-md py-2`}
                >
                  {/* TODO: Need to fix the mobile friendly view of the datecontrol custom */}
                  {activeMenu?.date_filter && (
                    <div className="w-full flex justify-end">
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex justify-end gap-2 sm:w-auto">
                          <AccordionDropdown
                            value={activeDateFilter}
                            onChange={ondateFilterChange}
                            options={dateFilterData ?? []}
                            placeholder="Select period..."
                          />
                        </div>

                        {isCustomDateFilter && (
                          <div className="flex justify-end items-center space-x-5 w-full">
                            {/* your date inputs */}
                            <div className="flex items-center justify-between border-2 border-gray-300 bg-white h-10 pl-3 rounded-lg text-sm focus:outline-none">
                              <ReactDatePicker
                                selectsRange={false}
                                minDate={minDate}
                                maxDate={maxDate}
                                wrapperClassName="bg-transparent focus:outline-none"
                                dateFormat={"MMM d, yyyy"}
                                selected={dateRange}
                                onChange={(update) => {
                                  setDateRange(update);
                                }}
                                placeholderText="Start date"
                                clearButtonClassName="text-primary hover:bg-primary rounded-md"
                                isClearable={true}
                                clear="close"
                              />
                              <div className="flex-1 pr-2">
                                <CalendarIcon
                                  width={20}
                                  height={20}
                                  fill="#9e9e9e"
                                />
                              </div>
                            </div>

                            <h2>-</h2>

                            <div className="flex items-center justify-between border-2 border-gray-300 bg-white h-10 pl-3 rounded-lg text-sm focus:outline-none">
                              <ReactDatePicker
                                selectsRange={false}
                                minDate={dateRange ?? minDate}
                                maxDate={maxDate}
                                wrapperClassName="bg-transparent focus:outline-none"
                                dateFormat={"MMM d, yyyy"}
                                selected={dateRange2}
                                onChange={(update) => {
                                  setDateRange2(update);
                                }}
                                placeholderText="End date"
                                clearButtonClassName="text-primary hover:bg-primary rounded-md"
                                isClearable={true}
                                clear="close"
                              />
                              <div className="flex-1 pr-2">
                                <CalendarIcon
                                  width={20}
                                  height={20}
                                  fill="#9e9e9e"
                                />
                              </div>
                            </div>

                            <button
                              disabled={
                                !dateRange && !dateRange2 ? true : false
                              }
                              onClick={() => {
                                if (
                                  moment(dateRange)._isValid &&
                                  moment(dateRange2)._isValid
                                ) {
                                  const dateFilter = {
                                    startDate:
                                      moment(dateRange).format("yyyy-MM-D"),
                                    endDate:
                                      moment(dateRange2).format("yyyy-MM-D"),
                                  };
                                  setLoadDate(true);
                                  setFilterDateRange(dateFilter);
                                  filterByDateRange(dateFilter);
                                }

                                if (
                                  !moment(dateRange)._isValid &&
                                  !moment(dateRange2)._isValid
                                ) {
                                  setFilterDateRange(null);
                                }
                              }}
                              className={`${
                                dateRange && dateRange2
                                  ? "bg-primary "
                                  : "bg-gray-300 text-black"
                              }hover:bg-primary text-white tracking-normal text-center font-semibold text-xs py-[10px] px-4 rounded-lg inline-flex items-center`}
                              // style={{ borderRadius: "1rem" }}
                            >
                              <h3 className="text-sm">
                                {loadDate ? (
                                  <span className="">
                                    {" "}
                                    <Spinner
                                      fill="#e2e3e4"
                                      width={18}
                                      height={18}
                                    />
                                  </span>
                                ) : (
                                  <span className="">Search</span>
                                )}
                              </h3>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* {activeMenu?.date_filter && (
                    <div>
                      <div className="flex flex-end gap-2 sm:w-auto">
                        <Select
                          className={classNames(
                            `${!isCustomDateFilter ? "w-full sm:w-auto" : "flex justify-end"} `,
                          )}
                          classNamePrefix="custom-select-option"
                          components={{
                            Option: ReportOption,
                            SingleValue: ReportOptionValue,
                          }}
                          value={activeDateFilter}
                          isClearable={false}
                          onChange={ondateFilterChange}
                          options={dateFilterData}
                        />
                      </div>

                      {isCustomDateFilter && (
                        <div className="flex justify-end space-x-5 items-center">
                          <div className="flex items-center justify-between border-2 border-gray-300 bg-white h-10 pl-3 rounded-lg text-sm focus:outline-none">
                            <ReactDatePicker
                              selectsRange={false}
                              minDate={minDate}
                              maxDate={maxDate}
                              wrapperClassName="bg-transparent focus:outline-none"
                              dateFormat={"MMM d, yyyy"}
                              selected={dateRange}
                              onChange={(update) => {
                                setDateRange(update);
                              }}
                              placeholderText="Start date"
                              clearButtonClassName="text-primary hover:bg-primary rounded-md"
                              isClearable={true}
                              clear="close"
                            />
                            <div className="flex-1 pr-2">
                              <CalendarIcon
                                width={20}
                                height={20}
                                fill="#9e9e9e"
                              />
                            </div>
                          </div>

                          <h2>-</h2>

                          <div className="flex items-center justify-between border-2 border-gray-300 bg-white h-10 pl-3 rounded-lg text-sm focus:outline-none">
                            <ReactDatePicker
                              selectsRange={false}
                              minDate={dateRange ?? minDate}
                              maxDate={maxDate}
                              wrapperClassName="bg-transparent focus:outline-none"
                              dateFormat={"MMM d, yyyy"}
                              selected={dateRange2}
                              onChange={(update) => {
                                setDateRange2(update);
                              }}
                              placeholderText="End date"
                              clearButtonClassName="text-primary hover:bg-primary rounded-md"
                              isClearable={true}
                              clear="close"
                            />
                            <div className="flex-1 pr-2">
                              <CalendarIcon
                                width={20}
                                height={20}
                                fill="#9e9e9e"
                              />
                            </div>
                          </div>

                          <button
                            disabled={!dateRange && !dateRange2 ? true : false}
                            onClick={() => {
                              if (
                                moment(dateRange)._isValid &&
                                moment(dateRange2)._isValid
                              ) {
                                const dateFilter = {
                                  startDate:
                                    moment(dateRange).format("yyyy-MM-D"),
                                  endDate:
                                    moment(dateRange2).format("yyyy-MM-D"),
                                };
                                setLoadDate(true);
                                setFilterDateRange(dateFilter);
                                filterByDateRange(dateFilter);
                              }

                              if (
                                !moment(dateRange)._isValid &&
                                !moment(dateRange2)._isValid
                              ) {
                                setFilterDateRange(null);
                              }
                            }}
                            className={`${
                              dateRange && dateRange2
                                ? "bg-primary "
                                : "bg-gray-300 text-black"
                            }hover:bg-primary text-white tracking-normal text-center font-semibold text-xs py-[10px] px-4 rounded-lg inline-flex items-center`}
                            // style={{ borderRadius: "1rem" }}
                          >
                            <h3 className="text-sm">
                              {loadDate ? (
                                <span className="">
                                  {" "}
                                  <Spinner
                                    fill="#e2e3e4"
                                    width={18}
                                    height={18}
                                  />
                                </span>
                              ) : (
                                <span className="">Search</span>
                              )}
                            </h3>
                          </button>
                        </div>
                      )}
                    </div>
                  )} */}
                </div>
              </div>
            )}

            {activeDateFilter?.value === "Custom" && (
              <div className="flex md:space-x-5 items-center justify-end">
                {/* date pickers etc (unchanged) */}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="mt-6">
            <div>
              {newsubscomponents?.map((config, idx) =>
                renderComponent(config, {
                  filterOption,
                  initialActiveReport,
                  filteredDatefilter,
                  accessOption,
                }),
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WithLoader(MainArea1);
